import { createApi } from '@reduxjs/toolkit/query/react';
import { AssignmentStatus, DurationType, Language, QuestionType, QuizMode, SurveyType } from '@shared/enum';
import { apiBaseQuery } from '@shared/functions';

export type ErrorObject = {
    [key: string]: string;
};

export interface MetaData {
    program_count: number;
    completed_program_count: number;
    course_count: number;
    lesson_count: number;
    closest_lesson_expiration: string;
}

export interface KPIConfig {
    bacground_color?: string;
    range_rules: {
        rule: string;
        color: string;
        value: number;
    }[];
}

export interface Lesson {
    lesson_id: number;
    lesson_name: string;
    text?: string;
    survey_mode_id?: number;
    survey_mode?: SurveyType;
    survey_id?: number;
    is_locked: boolean;
    completion_percentage: number;
    lesson_picture?: string | null;
    created_at?: string | null;
    assignment_from?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    type: LessonType;
    objective?: string;
    duration?: string;
    available_to?: string | null;
    file_url: string | null;
    assignment_id: number;
    course_lesson_id: number;
    lesson_attempt_status_id: number;
}

export interface LessonType {
    lesson_type_id: number;
    lesson_type: string;
    image?: string | null;
}

export interface Course {
    course_id: number;
    course_name: string;
    expiration?: string;
    created_at: string;
    objective?: string;
    completed_lessons: number;
    total_lessons: number;
    course_completion_percentage: number;
    image?: string | null;
    available_to?: string | null;
    lessons: Lesson[];
    lesson_types: LessonType[];
}

export interface Program {
    user_id: number;
    program_id: number;
    program: string;
    objective: string;
    completion_percentage: number;
    assignment_id: number;
    assignment_from: string | null;
}

export interface ProgramWithCourses extends Program {
    courses: Course[];
}

export interface ProgramWithCourse extends Program {
    course: Course;
}

export interface ProgramsRequest {
    language: Language;
}

export interface ProgramsResponse {
    status: number;
    message: string;
    data: {
        meta: MetaData;
        results: ProgramWithCourses[];
    };
    error: ErrorObject;
}

export interface ProgramRequest {
    language: Language;
    courseID: number;
    programID: number;
    assignmentID: number;
}

export interface ProgramResponse {
    status: number;
    message: string;
    data: ProgramWithCourse;
    error: ErrorObject;
}

export interface CreateLessonAttemptRequest {
    language: Language;
    lessonID: number;
    assignmentID: number;
    courseLessonID: number;
    impersonateUserId?: number;
}

export interface CreateLessonAttemptData {
    /** id of the freshly-created lms.lesson_attempt row; null if creation failed */
    lesson_attempt_id: number | null;
}

export interface CreateLessonAttemptResponse {
    status: number;
    message: string;
    data: CreateLessonAttemptData;
    error: ErrorObject;
}

export interface UpdateLessonAttemptStatusRequest {
    language: Language;
    lessonAttemptID: number;
    lessonAttemptStatusID: number;
}

export interface UpdateLessonAttemptStatusResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface CreateQuizAttemptRequest {
    language: Language;
    lessonID: number;
    mode: QuizMode;
}

export interface CreateQuizAttemptResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface QuizRequest {
    language: Language;
    surveyID: number;
    lessonID: number;
}

export interface Answer {
    is_visible: boolean;
    option: string;
    survey_question_id: number;
    id: number;
    is_correct_answer: boolean;
    ui_order: number;
    correct_answer_id?: number;
}

export interface Group {
    id: number;
    survey_question_id: number;
    name: string;
}

export interface Question {
    is_visible: boolean;
    correct_answer_explanation: string;
    is_answered: boolean;
    is_answered_correctly: boolean;
    type: string;
    question: string;
    survey_id: number;
    survey_question_id: number;
    ui_order: number;
    answers: Answer[];
    sub_questions: Answer[];
    groups: Group[];
    question_type_id: number;
    survey_mode?: number;
    survey_mode_id?: number;
    answer_text?: string;
    selected_rating?: number;
    selected_yesno?: boolean;
}

export interface QuizResponse {
    status: number;
    message: string;
    data: Question[];
    error: ErrorObject;
}

export interface CreateAnswerSurveyTypeRequest {
    language: Language;
    surveyID: number;
    questionID: number;
    answerIDs?: number[];
    answerText?: string;
    answerRating?: number;
    answerYesNo?: boolean;
    lessonID: number;
    isCompleted: boolean;
    completionPrct: number;
}

export interface CreateAnswerDragDropSurveyTypeRequest {
    language: Language;
    surveyID: number;
    questionID: number;
    answerMeta: { from: number; to: number[] }[];
    lessonID: number;
    isCompleted: boolean;
    completionPrct: number;
    type: QuestionType.connectingDots | QuestionType.dragDrop;
}

export interface CreateAnswerSurveyTypeResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface CreateAnswerTextTypeRequest {
    language: Language;
    lessonID: number;
    isCompleted: boolean;
    completionPrct: number;
}

export interface CreateAnswerTextTypeResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface ComputersRequest {
    language: Language;
    return_all: boolean;
}

export interface Computer {
    computer_id: number;
    computer_name: string;
    status: string;
    company_id?: number;
    current_user_id?: number | null;
    current_user_name?: string | null;
    current_lesson_id?: number | null;
    current_lesson_name?: string | null;
    current_lesson_mode_id?: number | null;
    current_lesson_mode_name?: string | null;
    current_course_lesson_id?: number | null;
    components?: ComputerComponent[] | null;
    launcher_version?: string | null;
    latest_launcher_version?: string | null;
    /** Backend: online launcher older than the latest release; a launcher restart installs it. */
    launcher_update_required?: boolean;
}

export interface ComputerComponent {
    name: string;
    status: string;
    note: string;
}

export interface ComputersResponse {
    status: number;
    message: string;
    data: Computer[];
    error: ErrorObject;
}

export interface SocketRequest {
    language: Language;
    computerID: number;
    group: string | null;
}

export interface SocketResponse {
    status: number;
    message: string;
    data: string;
    error: ErrorObject;
}

export interface AssignmentsRequest {
    language: Language;
}

export interface AssignmentRequest {
    language: Language;
    assignmentID: number;
}

export interface Assignment {
    assignment_id: number;
    user_id: number;
    assignment_name: string;
    full_name: string;
    created_at: string;
    available_to: string;
    status: string;
    type: string;
    learners: number;
    type_id: number;
    status_id: number;
    duration: number;
    duration_type: DurationType;
    is_editable: boolean;
}

export interface AssignmentsResponse {
    status: number;
    message: string;
    data: Assignment[];
    error: ErrorObject;
}

export interface AssignmentResponse {
    status: number;
    message: string;
    data: {
        meta: Assignment;
        program_ids: number[];
        user_groups_ids: number[];
        user_ids: number[];
    };
    error: ErrorObject;
}

export interface KpiResultsRequest {
    language: Language;
    assignmentID: number;
    lessonID: number;
    userID?: number;
    lesson_attemptID?: number;
}

export interface KPIResult {
    assignment_id: number;
    user_id: number;
    lesson_id: number;
    kpi_name: string;
    kpi_id: number;
    kpi_explanation: string;
    is_numeric: boolean;
    is_text: boolean;
    is_key: boolean;
    formatting: string;
    parent_kpi_id: number | null;
    config: string;
    value_numeric: string;
    value_text: string;
    score: string | null;
    goal: string | null;
    tooltip?: string;
    formatting_score: string | null;
    formatting_goal: string | null;
    ui_order: number;
}

export interface ParkingPosition {
    truck_front_left_x: number;
    truck_front_left_y: number;
    truck_front_right_x: number;
    truck_front_right_y: number;
    truck_rear_left_x: number;
    truck_rear_left_y: number;
    truck_rear_right_x: number;
    truck_rear_right_y: number;

    trailer_front_left_x: number;
    trailer_front_left_y: number;
    trailer_front_right_x: number;
    trailer_front_right_y: number;
    trailer_rear_left_x: number;
    trailer_rear_left_y: number;
    trailer_rear_right_x: number;
    trailer_rear_right_y: number;

    destination_front_left_x: number;
    destination_front_left_y: number;
    destination_front_right_x: number;
    destination_front_right_y: number;
    destination_rear_left_x: number;
    destination_rear_left_y: number;
    destination_rear_right_x: number;
    destination_rear_right_y: number;

    parking_position_version: number;
}

export interface Coordinate {
    x: number;
    y: number;
}

export interface KPIResultFormatted {
    assignment_id: number;
    user_id: number;
    lesson_id: number;
    kpi_name: string;
    kpi_id: number;
    kpi_explanation: string;
    is_numeric: boolean;
    is_text: boolean;
    parent_kpi_id: number | null;
    is_key: boolean;
    formatting: string;
    config: KPIConfig;
    value_numeric: string;
    value_text: string;
    score: string | null;
    goal: string | null;
    tooltip?: string;
    formatting_score: string | null;
    formatting_goal: string | null;
    ui_order: number;
}

export interface KpiResultsResponse {
    status: number;
    message: string;
    data: KPIResult[];
    parkingPosition: ParkingPosition;
    error: ErrorObject;
}

export interface AssignmentContent {
    program_id: number;
    course_id: number;
}

export interface CreateAssignmentRequest {
    language: Language;
    type: AssignmentStatus;
    assignmentName: string;
    assignmentTypeID: number;
    contents: AssignmentContent[];
    usersGroupsIds: number[];
    usersIds: number[];
    dueDate: string | null;
    duration: number | null;
    durationType: DurationType | null;
}

export interface UpdateAssignmentRequest extends CreateAssignmentRequest {
    assignmentID: number;
}

export interface CreateAssignmentResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface UpdateAssignmentResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface AssignmentTypesRequest {
    language: Language;
}

export interface AssignmentTypesResponse {
    status: number;
    message: string;
    data: {
        id: number;
        name: string;
    }[];
    error: ErrorObject;
}

export interface AssignmentsProgramsRequest {
    language: Language;
}

export interface AssignmentsProgramsMeta {
    id: number;
    name: string;
    tags: string[];
    created_by: string;
    created_at: string;
    type: 'Program' | 'Course';
    parent_id?: number;
}

export interface AssignmentsPrograms {
    program_id: number;
    program_name: string;
    program_tags: string;
    program_tags_array: string[];
    program_created_by: string;
    program_created_at: string;
    course_id: number;
    course_name: string;
    course_tags: string;
    course_tags_array: string[];
    course_created_by: string;
    course_created_at: string;
}

export interface AssignmentsProgramsResponse {
    status: number;
    message: string;
    data: AssignmentsPrograms[];
    error: ErrorObject;
}

export interface CreateUserNotesResponse {
    status: number;
    message: string;
    data: [];
    error: ErrorObject;
}
export interface CreateUserNotesRequest {
    language: Language;
    assignmentID: number;
    courseLessonID?: number;
    impersonateUserId?: number;
    note?: string | null;
    assignment_user_lesson_id?: number | null;
}

export interface CreateCustomLessonConfigResponse {
    status: number;
    message: string;
    data: [];
    error: ErrorObject;
}
export interface CreateCustomLessonConfigRequest {
    language: Language;
    assignmentID: number;
    courseLessonID?: number;
    impersonateUserId?: number;
    lesson_custom_config?: string | null;
    assignment_user_lesson_id?: number | null;
}

export const assignmentApi = createApi({
    reducerPath: 'assignmentApi',
    baseQuery: apiBaseQuery,
    tagTypes: [
        'Assignments',
        'Programs',
        'Program',
        'Quiz',
        'Computers',
        'AssignmentTypes',
        'AssignmentsPrograms',
        'Assignment',
    ],
    endpoints: (builder) => ({
        getAssignments: builder.query<AssignmentsResponse, AssignmentsRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_assignments',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                },
            }),
            providesTags: ['Assignments'],
        }),
        getPrograms: builder.query<ProgramsResponse, ProgramsRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_programs',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                },
            }),
            providesTags: ['Programs'],
        }),
        getKpiResults: builder.query<KpiResultsResponse, KpiResultsRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_kpi_results',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                    assignment_id: data.assignmentID,
                    lesson_id: data.lessonID,
                    user_id: data.userID,
                    lesson_attempt_id: data.lesson_attemptID,
                },
            }),
            providesTags: ['Programs'],
        }),
        getProgram: builder.query<ProgramResponse, ProgramRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_program',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                    program_id: data.programID,
                    course_id: data.courseID,
                    assignment_id: data.assignmentID,
                },
            }),
            providesTags: ['Program'],
        }),
        getAssignment: builder.query<AssignmentResponse, AssignmentRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_assignment',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                    assignment_id: data.assignmentID,
                },
            }),
            providesTags: ['Assignment'],
        }),
        deactivateAssignment: builder.mutation<AssignmentResponse, { assignmentID: number }>({
            query: (data) => ({
                url: 'v1/assignment/deactivate_assignment',
                method: 'POST',
                credentials: 'include',
                body: {
                    assignment_id: data.assignmentID,
                },
            }),
            invalidatesTags: ['Assignments', 'Assignment'],
        }),
        createAssignment: builder.mutation<CreateAssignmentResponse, CreateAssignmentRequest>({
            query: (data) => ({
                url: 'v1/assignment/create_assignment',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    type: data.type,
                    assignment_name: data.assignmentName,
                    assignment_type_id: data.assignmentTypeID,
                    contents: data.contents,
                    user_group_ids: data.usersGroupsIds,
                    user_ids: data.usersIds,
                    due_date: data.dueDate,
                    duration: data.duration,
                    duration_type: data.durationType,
                },
            }),

            invalidatesTags: ['Assignments', 'Assignment'],
        }),
        updateAssignment: builder.mutation<UpdateAssignmentResponse, UpdateAssignmentRequest>({
            query: (data) => ({
                url: 'v1/assignment/update_assignment',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    assignment_id: data.assignmentID,
                    type: data.type,
                    assignment_name: data.assignmentName,
                    assignment_type_id: data.assignmentTypeID,
                    contents: data.contents,
                    user_group_ids: data.usersGroupsIds,
                    user_ids: data.usersIds,
                    due_date: data.dueDate,
                    duration: data.duration,
                    duration_type: data.durationType,
                },
            }),
            invalidatesTags: ['Assignments', 'Assignment'],
        }),
        createLessonAttempt: builder.mutation<CreateLessonAttemptResponse, CreateLessonAttemptRequest>({
            query: (data) => ({
                url: 'v1/assignment/create_lesson_attempt',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    lesson_id: data.lessonID,
                    assignment_id: data.assignmentID,
                    course_lesson_id: data.courseLessonID,
                    impersonate_user_id: data.impersonateUserId,
                },
            }),
            invalidatesTags: ['Program'],
        }),
        updateLessonAttemptStatus: builder.mutation<
            UpdateLessonAttemptStatusResponse,
            UpdateLessonAttemptStatusRequest
        >({
            query: (data) => ({
                url: 'v1/assignment/update_lesson_attempt_status',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    lesson_attempt_id: data.lessonAttemptID,
                    lesson_attempt_status_id: data.lessonAttemptStatusID,
                },
            }),
            invalidatesTags: ['Program'],
        }),
        createQuizAttempt: builder.mutation<CreateQuizAttemptResponse, CreateQuizAttemptRequest>({
            query: (data) => ({
                url: 'v1/assignment/create_quiz_attempt',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    lesson_id: data.lessonID,
                    survey_mode: data.mode,
                },
            }),
            invalidatesTags: ['Quiz'],
        }),
        createAnswerSurveyType: builder.mutation<CreateAnswerSurveyTypeResponse, CreateAnswerSurveyTypeRequest>({
            query: (data) => ({
                url: 'v1/assignment/create_answer_survey_type',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    survey_id: data.surveyID,
                    survey_question_id: data.questionID,
                    selected_answer_option_ids: data.answerIDs,
                    answer_text: data.answerText,
                    selected_rating: data.answerRating,
                    selected_yesno: data.answerYesNo,
                    lesson_id: data.lessonID,
                    completion_prct: data.completionPrct,
                    is_completed: data.isCompleted,
                },
            }),
            invalidatesTags: ['Program', 'Quiz'],
        }),
        createAnswerDragDropSurveyType: builder.mutation<
            CreateAnswerSurveyTypeResponse,
            CreateAnswerDragDropSurveyTypeRequest
        >({
            query: (data) => ({
                url: 'v1/assignment/create_answer_drag_drop_survey_type',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    survey_id: data.surveyID,
                    survey_question_id: data.questionID,
                    answer_meta: data.answerMeta,
                    lesson_id: data.lessonID,
                    completion_prct: data.completionPrct,
                    is_completed: data.isCompleted,
                    type: data.type,
                },
            }),
            invalidatesTags: ['Program', 'Quiz'],
        }),
        createAnswerTextType: builder.mutation<CreateAnswerTextTypeResponse, CreateAnswerTextTypeRequest>({
            query: (data) => ({
                url: 'v1/assignment/create_answer_text_type',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    lesson_id: data.lessonID,
                    completion_prct: data.completionPrct,
                    is_completed: data.isCompleted,
                },
            }),
            invalidatesTags: ['Program'],
        }),
        getQuizData: builder.query<QuizResponse, QuizRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_quiz',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                    survey_id: data.surveyID,
                    lesson_id: data.lessonID,
                },
            }),
            providesTags: ['Quiz'],
        }),
        getComputers: builder.query<ComputersResponse, ComputersRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_computers',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                    return_all: data.return_all,
                },
            }),
            providesTags: ['Computers'],
        }),
        createSocketToken: builder.mutation<SocketResponse, SocketRequest>({
            query: (data) => ({
                url: `v1/assignment/retrieve_socket_token`,
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    computer_id: data.computerID,
                    group: data.group,
                },
            }),
        }),
        getAssignmentTypes: builder.query<AssignmentTypesResponse, AssignmentTypesRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_assignment_types',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                },
            }),
            providesTags: ['AssignmentTypes'],
        }),
        getAssignmentsPrograms: builder.query<AssignmentsProgramsResponse, AssignmentsProgramsRequest>({
            query: (data) => ({
                url: 'v1/assignment/retrieve_assignments_programs',
                method: 'GET',
                credentials: 'include',
                params: {
                    language: data.language.toLowerCase(),
                },
            }),
            providesTags: ['AssignmentsPrograms'],
        }),
        saveUserNotes: builder.mutation<CreateUserNotesResponse, CreateUserNotesRequest>({
            query: (data) => ({
                url: 'v1/assignment/saveUserNotes',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    assignment_id: data.assignmentID,
                    course_lesson_id: data.courseLessonID,
                    impersonate_user_id: data.impersonateUserId,
                    note: data.note,
                    assignment_user_lesson_id: data.assignment_user_lesson_id,
                },
            }),
            invalidatesTags: ['Program'],
        }),
        saveLessonCustonConfig: builder.mutation<CreateCustomLessonConfigResponse, CreateCustomLessonConfigRequest>({
            query: (data) => ({
                url: 'v1/assignment/saveLessonCustomConfig',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    assignment_id: data.assignmentID,
                    course_lesson_id: data.courseLessonID,
                    impersonate_user_id: data.impersonateUserId,
                    lesson_custom_config: data.lesson_custom_config,
                    assignment_user_lesson_id: data.assignment_user_lesson_id,
                },
            }),
            invalidatesTags: ['Program'],
        }),
    }),
});

export const {
    useGetProgramsQuery,
    useGetProgramQuery,
    useGetKpiResultsQuery,
    useGetAssignmentsQuery,
    useCreateQuizAttemptMutation,
    useCreateLessonAttemptMutation,
    useUpdateLessonAttemptStatusMutation,
    useGetQuizDataQuery,
    useCreateAnswerSurveyTypeMutation,
    useCreateAnswerDragDropSurveyTypeMutation,
    useCreateAnswerTextTypeMutation,
    useGetComputersQuery,
    useCreateSocketTokenMutation,
    useGetAssignmentTypesQuery,
    useGetAssignmentsProgramsQuery,
    useCreateAssignmentMutation,
    useDeactivateAssignmentMutation,
    useUpdateAssignmentMutation,
    useGetAssignmentQuery,
    useSaveUserNotesMutation,
    useSaveLessonCustonConfigMutation,
} = assignmentApi;
